<template>
    <div class="w-100 align-self-start fill-height">
        <v-card flat color="transparent" class="mb-14">
            <v-card-title>
                <span class="secondary--text">Add New Application</span>
            </v-card-title>

            <v-row justify="center" no-gutters>
                <v-col cols="4">
                    <v-stepper v-model="activeStep" class="transparent">
                        <v-stepper-header>
                            <v-stepper-step color="primary" :complete="activeStep > 1" step="1">Application</v-stepper-step>
                            <v-divider></v-divider>
                            <v-stepper-step color="primary" step="2">Application Resources</v-stepper-step>
                        </v-stepper-header>
                    </v-stepper>
                </v-col>
            </v-row>

            <app-store v-if="activeStep === 1" selection-only @application-selected="selectApplication"></app-store>

            <div v-else class="px-4 pb-16">
                <v-card>
                    <v-list-item class="py-1">
                        <v-list-item-avatar tile size="52">
                            <v-img :src="selectedApplication.icon_url" contain></v-img>
                        </v-list-item-avatar>
                        <v-list-item-content>
                            <v-list-item-title class="secondary--text header-font font-weight-bold">
                                {{ selectedVersion.name }}
                            </v-list-item-title>
                            <v-list-item-subtitle>{{ selectedVersion.short_description }}</v-list-item-subtitle>
                            <div class="mt-1">
                                <v-chip v-for="tag in selectedVersionTags" :key="tag" small class="mr-2 my-1">{{ tag }}</v-chip>
                            </div>
                        </v-list-item-content>
                        <v-list-item-action>
                            <v-btn text color="secondary" :disabled="installing || !!createdApplication" @click="changeApplication">
                                <v-icon left small>mdi-swap-horizontal</v-icon>
                                Change Application
                            </v-btn>
                        </v-list-item-action>
                    </v-list-item>
                </v-card>

                <app-scale :application="resourcePreviewApplication" selection-only hide-header @resource-selected="selectedResource = $event"></app-scale>

                <v-card class="mt-6">
                    <v-card-title class="py-3 text-body-1" @click="configurationExpanded = !configurationExpanded">
                        <v-icon left>mdi-tune-variant</v-icon>
                        Application configuration
                        <v-spacer></v-spacer>
                        <div v-if="!configurationExpanded" class="d-flex align-center flex-wrap justify-end">
                            <span v-for="summary in configurationSummary" :key="summary.label" class="caption ml-4">
                                <v-icon small left>{{ summary.icon }}</v-icon>
                                {{ summary.value }}
                            </span>
                        </div>
                        <v-icon right>{{ configurationExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                    </v-card-title>
                    <v-expand-transition>
                        <v-card-text v-if="configurationExpanded">
                            <application-config-editor
                                v-if="applicationConfig"
                                :value="applicationConfig"
                                :image="selectedVersion"
                                :nodepool="selectedNodePool"
                                :loading="configLoading"
                                :is-trial="isTrialSpace"
                                @input="updateApplicationConfig"></application-config-editor>
                            <dashboard-loader v-else class="my-7">Loading settings</dashboard-loader>
                        </v-card-text>
                    </v-expand-transition>
                </v-card>

                <v-card-actions class="mt-6 px-0">
                    <div>
                        <v-btn color="white" :disabled="installing || !!createdApplication" @click="changeApplication">
                            <v-icon left>mdi-arrow-left</v-icon>
                            Back
                        </v-btn>
                    </div>
                    <v-spacer></v-spacer>
                    <v-card v-if="selectedResourceDetails.length" class="px-4 py-2">
                        <span v-for="(detail, index) in selectedResourceDetails" :key="detail.label" class="caption">
                            <span v-if="index" class="mx-2">&middot;</span>
                            <strong>{{ detail.label }}:</strong>
                            {{ detail.value }}
                        </span>
                    </v-card>
                    <v-spacer></v-spacer>
                    <div>
                        <v-btn color="primary" :disabled="!selectedResource || installing" :loading="installing" @click="installApplication">
                            Create Application
                            <v-icon right small>mdi-rocket-launch-outline</v-icon>
                        </v-btn>
                    </div>
                </v-card-actions>
            </div>

            <v-dialog v-model="nameDialog" width="40%" persistent>
                <v-card>
                    <v-card-title class="flex-nowrap align-start">
                        Pick a name for your application
                        <v-spacer></v-spacer>
                        <v-btn icon @click="nameDialog = false"><v-icon>clear</v-icon></v-btn>
                    </v-card-title>
                    <v-card-subtitle class="caption">Application name already exists: {{ appNameTaken }}</v-card-subtitle>
                    <v-card-text>
                        <v-form v-model="appNameIsValid" @submit.prevent="installApplication">
                            <v-text-field v-model="appName" required :rules="[rules.nonEmpty]" outlined>
                                <template v-slot:label>Type a name here</template>
                            </v-text-field>
                        </v-form>
                        <v-alert v-if="errorContent" type="warning" text icon="mdi-alert-circle-outline">
                            {{ errorContent }}
                        </v-alert>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn text color="primary" :disabled="!appNameIsValid" :loading="installing" @click="installApplication">Create</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>
        </v-card>
    </div>
</template>

<script>
import { scaling } from '@/mixins/scaling'
import { ipcOptions, publicSubdomainRule } from '@/utils'
import { mapGetters } from 'vuex'
import { createBackendErrorHandler } from '@/utils/errorMessages'
import * as applicationErrors from '@/modules/application/errors'
import * as lifecycleErrors from '@/modules/snapshot/errors/lifecycle'
import * as permissionsErrors from '@/utils/errors/permissions'
import * as snapshotErrors from '@/modules/snapshot/errors/snapshots'

const { getBackendErrorMessage } = createBackendErrorHandler(applicationErrors, lifecycleErrors, permissionsErrors, snapshotErrors)

const AppStore = () => import('./AppStore.vue')
const AppScale = () => import('./AppScale.vue')
const ApplicationConfigEditor = () => import('@/modules/application/components/ApplicationConfigEditor.vue')
const DashboardLoader = () => import('@/components/DashboardLoader.vue')

export default {
    name: 'AppInstall',
    components: { AppStore, AppScale, ApplicationConfigEditor, DashboardLoader },
    mixins: [scaling],
    data() {
        return {
            activeStep: 1,
            selectedApplication: null,
            selectedVersion: null,
            selectedResource: null,
            createdApplication: null,
            appName: '',
            appNameTaken: '',
            appNameIsValid: false,
            nameDialog: false,
            errorContent: '',
            installing: false,
            applicationConfig: null,
            configLoading: false,
            configDirty: false,
            configurationExpanded: false,
            rules: {
                nonEmpty: value => value.length > 0 || 'Required field.'
            }
        }
    },
    computed: {
        ...mapGetters('spaceStore', ['isTrialSpace']),
        resourcePreviewApplication() {
            return {
                long_id: this.appName,
                active_resource: this.selectedResource?.ncu ? 'ncu' : this.selectedResource?.node_pool ? 'node_pool' : null,
                ncu: this.selectedResource?.ncu || null,
                node_pool: this.selectedResource?.node_pool || null
            }
        },
        selectedResourceDetails() {
            if (this.selectedResource?.ncu) {
                const resource = this.currentNCU(this.selectedResource.ncu)
                if (!resource) return []
                return [
                    { label: 'Processor', value: resource.cpu },
                    { label: 'RAM', value: `${this.toLocaleFixed(resource.memory, 0)} GB` }
                ]
            }

            if (!this.selectedResource?.node_pool) return []
            const resource = this.currentLocalNodePool(this.selectedResource.node_pool) || this.currentNodePool(this.selectedResource.node_pool)
            if (!resource) return []

            return [
                resource.cpu ? { label: 'Processor', value: resource.cpu } : null,
                resource.memory ? { label: 'RAM', value: `${this.toLocaleFixed(resource.memory, 0)} GB` } : null,
                resource.gpu_type ? { label: 'Accelerator', value: resource.gpu_type } : null,
                resource.vram ? { label: 'HBM', value: `${this.toLocaleFixed(resource.vram, 0)} GB` } : null,
                resource.ssd ? { label: 'Temp storage', value: `${this.toLocaleFixed(resource.ssd, 0)} GB` } : null
            ].filter(Boolean)
        },
        selectedVersionTags() {
            if (!this.selectedVersion?.tags) return []
            return Object.entries(this.selectedVersion.tags).flatMap(([tagType, tagNames]) => {
                const names = Array.isArray(tagNames) ? tagNames : [tagNames]
                return names.map(tagName => `${tagType}:${tagName}`)
            })
        },
        selectedNodePool() {
            if (!this.selectedResource?.node_pool) return null
            return this.currentLocalNodePool(this.selectedResource.node_pool) || this.currentNodePool(this.selectedResource.node_pool)
        },
        isFractionalNodePool() {
            return Boolean(
                this.selectedNodePool &&
                    (this.selectedNodePool.node_pool?.includes('-hami') || (this.selectedNodePool.cloud === 'switch' && this.selectedNodePool.gpu_type))
            )
        },
        configurationSummary() {
            if (!this.applicationConfig) return [{ label: 'Loading', value: 'Loading settings', icon: 'mdi-loading mdi-spin' }]
            const enabledAddons = this.applicationConfig.addons?.filter(addon => addon.enabled).length || 0
            const ipcMode = ipcOptions.find(option => option.value === this.applicationConfig.ipc_mode)?.text || 'Nowhere'
            const summary = [
                { label: 'Add-ons', value: `${enabledAddons} add-on${enabledAddons === 1 ? '' : 's'}`, icon: 'mdi-puzzle-outline' },
                { label: 'Inactivity', value: `${this.applicationConfig.inactivity_limit}h inactivity`, icon: 'mdi-timer-outline' },
                {
                    label: 'Shared',
                    value: this.applicationConfig.shared ? 'Shared' : 'Not shared',
                    icon: this.applicationConfig.shared ? 'mdi-account-multiple-outline' : 'mdi-account-outline'
                },
                {
                    label: 'Session data',
                    value: this.applicationConfig.persistent_home_area ? 'Persistent home' : 'Temporary home',
                    icon: this.applicationConfig.persistent_home_area ? 'mdi-content-save-outline' : 'mdi-delete-clock-outline'
                },
                {
                    label: 'Connectivity',
                    value:
                        this.applicationConfig.ipc_mode === 'public' && this.applicationConfig.public_subdomain
                            ? `Public access (${this.applicationConfig.public_subdomain})`
                            : ipcMode,
                    icon: 'mdi-connection'
                }
            ]
            if (this.isFractionalNodePool) {
                summary.push({
                    label: 'Allocation',
                    value: `${this.applicationConfig.hami_resource_limit || 100}% allocation`,
                    icon: 'mdi-chart-donut'
                })
            }
            return summary
        }
    },
    methods: {
        async selectApplication({ application, version }) {
            this.selectedApplication = application
            this.selectedVersion = version
            this.selectedResource = null
            this.createdApplication = null
            this.appName = version.name
            this.activeStep = 2
            this.configurationExpanded = false
            this.configDirty = false
            await this.fetchApplicationConfigDefaults()
        },
        changeApplication() {
            this.activeStep = 1
            this.selectedResource = null
            this.applicationConfig = null
        },
        async fetchApplicationConfigDefaults() {
            this.configLoading = true
            this.applicationConfig = null
            try {
                const { data } = await this.$axios.get(`/snapshots/${this.$route.params.snid}/applications/config-defaults/${this.selectedVersion.imid}`)
                this.applicationConfig = {
                    ...data,
                    addons: data.addons || [],
                    persistent_home_area: data.home_mount_type === 'ceph_direct'
                }
            } catch (error) {
                const snackBarText = getBackendErrorMessage(error, 'There was an error whilst fetching application settings')
                if (snackBarText) {
                    this.$store.dispatch('showSnackBar', {
                        snackBarText,
                        snackBarIcon: 'error'
                    })
                }
            } finally {
                this.configLoading = false
            }
        },
        updateApplicationConfig(config) {
            this.applicationConfig = config
            this.configDirty = true
        },
        async installApplication() {
            // NC-3133: validate before creating anything — a rejected config
            // must not leave a partially configured application behind.
            if (this.applicationConfig?.ipc_mode === 'public') {
                const valid = publicSubdomainRule(this.applicationConfig.public_subdomain)
                if (valid !== true) {
                    this.$store.dispatch('showSnackBar', { snackBarText: valid, snackBarIcon: 'error', snackBarTimeout: 10000 })
                    return
                }
            }
            this.installing = true
            this.errorContent = ''
            try {
                if (!this.createdApplication) {
                    const { data } = await this.$axios.post(
                        `/snapshots/${this.$route.params.snid}/applications`,
                        {
                            long_id: this.appName,
                            short_id: window.crypto.getRandomValues(new Uint32Array(1))[0].toString(),
                            imid: this.selectedVersion.imid,
                            pars: '{}',
                            description: this.selectedVersion.short_description
                        },
                        { timeout: 30000 }
                    )
                    this.createdApplication = data
                }
                await this.$axios.post(`/applications/${this.createdApplication.aid}/resource`, this.selectedResource)
                if (this.configDirty && this.applicationConfig) {
                    await this.$axios.put(`/applications/${this.createdApplication.aid}/config`, {
                        shared: this.applicationConfig.shared,
                        inactivity_limit: this.applicationConfig.inactivity_limit,
                        home_mount_type: this.applicationConfig.persistent_home_area ? 'ceph_direct' : 'emptydir',
                        shared_editable: this.applicationConfig.shared_editable,
                        ipc_mode: this.applicationConfig.ipc_mode,
                        public_subdomain:
                            this.applicationConfig.ipc_mode === 'public' ? (this.applicationConfig.public_subdomain || '').trim().toLowerCase() : null,
                        hami_resource_limit: this.applicationConfig.hami_resource_limit,
                        addons: this.applicationConfig.addons
                    })
                }
                await Promise.all([
                    this.$store.dispatch('appStore/fetchAllUserApps', { iid: this.$route.params.iid, startFirstApp: false }),
                    this.$store.dispatch('snapshotStore/fetchApplications', this.$route.params.snid)
                ])
                this.$router.push({ name: 'snapshot-applications', params: this.$route.params })
            } catch (error) {
                const reasons = error?.response?.data?.reason
                const duplicateName = Array.isArray(reasons) && reasons[0].includes('UniqueViolation')
                if (duplicateName) {
                    if (!this.nameDialog) {
                        this.appNameTaken = this.appName
                        this.appName = ''
                        this.nameDialog = true
                    } else {
                        this.errorContent = 'Another application uses the name you chose for this application, please choose another name'
                    }
                } else {
                    this.nameDialog = false
                    const snackBarText = getBackendErrorMessage(
                        error,
                        this.createdApplication
                            ? 'The application was created, but setting its resource failed. Please select the resource again.'
                            : error.description || error.message
                    )
                    if (snackBarText) {
                        this.$store.dispatch('showSnackBar', {
                            snackBarText,
                            snackBarTimeout: 10000,
                            snackBarIcon: 'error'
                        })
                    }
                }
            } finally {
                this.installing = false
            }
        }
    }
}
</script>
